// Statewide web design service-area pages. Each area links back to /colorado-web-design.
export type ServiceArea = {
  slug: string;
  path: string;
  name: string;
  title: string;
  description: string;
  heroLead: string;
  intro: string;
  towns: string[];
  local: { title: string; body: string }[];
  faqs: { q: string; a: string }[];
  nearby: { label: string; to: string }[];
};

const statewide = { label: "Colorado Web Design", to: "/colorado-web-design" };

export const SERVICE_AREAS: Record<string, ServiceArea> = {
  boulder: {
    slug: "boulder",
    path: "/web-design-boulder-colorado",
    name: "Boulder",
    title: "Web Design in Boulder, CO | Colorado Web Impressions",
    description: "Boulder web design for local businesses: fast, mobile-first websites with clear pricing. Starter sites from $1,850 with a year of hosting included.",
    heroLead: "Boulder websites built to earn",
    intro: "Boulder customers research carefully before they call. Your website needs to look credible, load fast and explain exactly what you do, where you work and what it costs to get started.",
    towns: ["Boulder", "Louisville", "Lafayette", "Superior", "Longmont", "Niwot", "Gunbarrel"],
    local: [
      { title: "Credibility first", body: "Real photos, real reviews and plain-language service pages help careful Boulder buyers trust you quickly." },
      { title: "Show your full service area", body: "Name Louisville, Lafayette, Superior and Longmont on the site so nearby customers know you serve them." },
      { title: "Fast on every phone", body: "Lightweight pages load quickly for customers searching on the go." },
    ],
    faqs: [
      { q: "Do you work with Boulder businesses remotely?", a: "Yes. Our office is in Colorado Springs, and we build for Boulder businesses over phone and video calls, so you don't have to drive anywhere." },
      { q: "How much does a Boulder website cost?", a: "Websites start at $1,850 for 4 pages. Pro is $2,750 for 10 pages, and Expert is $5,950 for up to 50 pages. Every package includes one year of hosting and SSL." },
    ],
    nearby: [statewide, { label: "Fort Collins", to: "/web-design-fort-collins-colorado" }, { label: "Denver", to: "/web-design-denver-colorado" }],
  },
  denver: {
    slug: "denver",
    path: "/web-design-denver-colorado",
    name: "Denver",
    title: "Web Design in Denver, CO | Colorado Web Impressions",
    description: "Denver web design for contractors and small businesses. Mobile-first websites from $1,850, typically built in under 7 business days.",
    heroLead: "Denver websites built to win",
    intro: "Denver is a crowded market. A clear, fast website that names your services and neighborhoods helps you stand out from bigger competitors without a big-agency budget.",
    towns: ["Denver", "Aurora", "Lakewood", "Englewood", "Arvada", "Westminster", "Centennial", "Littleton"],
    local: [
      { title: "Stand out in a big metro", body: "Dedicated service pages help you show up for the exact jobs Denver customers search for." },
      { title: "Neighborhood coverage", body: "We list the metro areas you actually serve so customers in Aurora or Lakewood know you work there." },
      { title: "Built to turn visits into calls", body: "Click-to-call, short quote forms and clear pricing make it easy for customers to contact you." },
    ],
    faqs: [
      { q: "Do you build websites for Denver businesses?", a: "Yes. We're based in Colorado Springs and build websites for businesses across the Denver metro." },
      { q: "How fast can my Denver website launch?", a: "Most websites are built in under 7 business days once we have your content. Larger projects are scoped before we start." },
    ],
    nearby: [statewide, { label: "Boulder", to: "/web-design-boulder-colorado" }, { label: "Castle Rock", to: "/web-design-castle-rock-colorado" }],
  },
  "fort-collins": {
    slug: "fort-collins",
    path: "/web-design-fort-collins-colorado",
    name: "Fort Collins",
    title: "Web Design in Fort Collins, CO | Colorado Web Impressions",
    description: "Fort Collins web design for Northern Colorado businesses. Fast, mobile-first websites from $1,850 with a year of hosting included.",
    heroLead: "Fort Collins websites built to grow",
    intro: "Northern Colorado is growing fast. A modern website helps Fort Collins, Loveland and Windsor customers find you, trust you and call you.",
    towns: ["Fort Collins", "Loveland", "Windsor", "Timnath", "Wellington", "Greeley", "Berthoud"],
    local: [
      { title: "Cover Northern Colorado", body: "Name Loveland, Windsor, Timnath and Greeley so customers across the region know you serve them." },
      { title: "Clear service pages", body: "One page per service makes it easier for customers and search engines to understand what you do." },
      { title: "Easy to update", body: "Change photos, hours or specials without waiting on a developer." },
    ],
    faqs: [
      { q: "Do you serve Loveland and Windsor too?", a: "Yes. We build for businesses throughout Northern Colorado, including Loveland, Windsor, Timnath and Greeley." },
      { q: "What's included with a Fort Collins website?", a: "Every package includes one year of hosting, SSL and a built-in traffic dashboard. Extra pages are $85 each." },
    ],
    nearby: [statewide, { label: "Boulder", to: "/web-design-boulder-colorado" }, { label: "Denver", to: "/web-design-denver-colorado" }],
  },
  durango: {
    slug: "durango",
    path: "/web-design-durango-colorado",
    name: "Durango",
    title: "Web Design in Durango, CO | Colorado Web Impressions",
    description: "Durango web design for Southwest Colorado businesses. Mobile-first websites from $1,850, built remotely by a Colorado team.",
    heroLead: "Durango websites built to book",
    intro: "Durango businesses serve locals and visitors alike. Your website should work for both: clear services, seasonal hours, easy directions and a fast way to call or book.",
    towns: ["Durango", "Bayfield", "Ignacio", "Hesperus", "Pagosa Springs", "Cortez", "Silverton"],
    local: [
      { title: "Locals and visitors", body: "Pages that speak to year-round customers and seasonal visitors without confusing either one." },
      { title: "Seasonal updates made easy", body: "Update hours, specials and photos yourself as the seasons change." },
      { title: "Works on weak signal", body: "Lightweight pages load quickly, even on slower mountain connections." },
    ],
    faqs: [
      { q: "Can you build my Durango website without meeting in person?", a: "Yes. We work with Southwest Colorado businesses by phone and video calls from our Colorado Springs office." },
      { q: "How much does a Durango website cost?", a: "Websites start at $1,850 for 4 pages, with one year of hosting and SSL included." },
    ],
    nearby: [statewide, { label: "Western Slope", to: "/web-design-western-slope-colorado" }, { label: "Pueblo", to: "/web-design-pueblo-colorado" }],
  },
  "western-slope": {
    slug: "western-slope",
    path: "/web-design-western-slope-colorado",
    name: "the Western Slope",
    title: "Western Slope Web Design | Grand Junction to Glenwood | CWI",
    description: "Web design for Western Slope businesses in Grand Junction, Montrose, Glenwood Springs and beyond. Websites from $1,850 with hosting included.",
    heroLead: "Western Slope websites built to be found",
    intro: "From Grand Junction to Glenwood Springs, Western Slope customers often drive long distances for the right business. A clear website shows exactly which towns you serve and makes it easy to call.",
    towns: ["Grand Junction", "Fruita", "Palisade", "Montrose", "Delta", "Glenwood Springs", "Rifle", "Carbondale"],
    local: [
      { title: "Spread-out service areas", body: "Name every town you cover so customers across the valley know you'll come to them." },
      { title: "Built for local trades and tourism", body: "Layouts that work for contractors, wineries, outfitters and local shops." },
      { title: "Statewide team, local focus", body: "A Colorado team that understands businesses outside the Front Range." },
    ],
    faqs: [
      { q: "Do you work with Grand Junction and Montrose businesses?", a: "Yes. We build websites for businesses across the Western Slope, working remotely from our Colorado Springs office." },
      { q: "What do Western Slope websites cost?", a: "Starter is $1,850 (4 pages), Pro is $2,750 (10 pages) and Expert is $5,950 (up to 50 pages). Each includes one year of hosting." },
    ],
    nearby: [statewide, { label: "Grand Junction marketing", to: "/grand-junction-digital-marketing" }, { label: "Durango", to: "/web-design-durango-colorado" }],
  },
};
